/**
 * Ticket System Type Definitions
 * Corresponds to backend DTOs and enums for the ticket management system
 */

// Enums
export enum TicketType {
  // Content moderation. These are the reasons a report is filed against something, and they are
  // what ReportContentDialog offers.
  ContentInappropriate = 0,
  ContentSpam = 1,
  ContentDuplicate = 2,
  ContentVandalism = 3,
  ContentCopyright = 4,

  // Location-specific reports.
  LocationPrivateProperty = 10,
  LocationMissing = 11,
  LocationInaccessible = 12,
  LocationSafety = 13,
  /** Hours, prices, directions or description are out of date. */
  LocationOutdatedInfo = 14,

  // User support. These are what somebody opening a ticket about the app itself picks from.
  Bug = 20,
  FeatureRequest = 21,
  Question = 22,
  AccountIssue = 23,
  TechnicalSupport = 24,

  // Validation (30-39). Not complaints: a creator's own to-do on an adventure ("check the cover
  // photo", "verify on site"). Opened by the adventure's managers, moderators or the importer.
  ValidateCoverPhoto = 30,
  ValidateOnSite = 31,
  ValidateCoordinates = 32,
  ValidateHoursAccess = 33,
  ValidateContent = 34,

  /** Someone claims an adventure another account created. Decided only by site Moderators/Admins. */
  AdventureOwnershipClaim = 40,

  Other = 99,
}

/** Who must act on a ticket now. The sweep raises community tickets after 7 and 14 days. */
export enum TicketEscalationLevel {
  Owner = 0,
  Mods = 1,
  SystemModerators = 2,
}

export const TicketEscalationLabels: Record<TicketEscalationLevel, string> = {
  [TicketEscalationLevel.Owner]: 'Owner',
  [TicketEscalationLevel.Mods]: 'Mods',
  [TicketEscalationLevel.SystemModerators]: 'System Moderators',
}

/** Who a comment on an ownership claim is for. Every other ticket's comments are Everyone. */
export enum TicketCommentParty {
  Everyone = 0,
  Claimant = 1,
  Owner = 2,
}

export const TicketCommentPartyLabels: Record<TicketCommentParty, string> = {
  [TicketCommentParty.Everyone]: 'Everyone',
  [TicketCommentParty.Claimant]: 'Claimant only',
  [TicketCommentParty.Owner]: 'Owner only',
}

/** Owner kind (API OwnerEntityType). */
export enum OwnerEntityType {
  User = 0,
  Group = 1,
  Business = 2,
}

export function isOwnershipClaimType(type: TicketType | number): boolean {
  return type === TicketType.AdventureOwnershipClaim
}

/** Validation types are the 30-39 band (API: TicketTypes.IsValidation). */
export function isValidationTicketType(type: TicketType | number): boolean {
  return type >= 30 && type <= 39
}

export const VALIDATION_TICKET_TYPES: TicketType[] = [
  TicketType.ValidateCoverPhoto,
  TicketType.ValidateOnSite,
  TicketType.ValidateCoordinates,
  TicketType.ValidateHoursAccess,
  TicketType.ValidateContent,
]

export enum TicketPriority {
  Low = 0,
  Medium = 1,
  High = 2,
  Critical = 3,
}

export enum TicketStatus {
  Pending = 0,
  Claimed = 1,
  InProgress = 2,
  AwaitingUser = 3,
  Resolved = 4,
  Closed = 5,
  Escalated = 6,
}

export enum TargetType {
  Cache = 0,
  Adventure = 1,
  POI = 2,
  Location = 3,
  Group = 4,
  User = 5,
  Discussion = 6,
  Post = 7,
}

// DTOs
export interface TicketSummaryDto {
  id: string
  type: TicketType
  priority: TicketPriority
  status: TicketStatus
  title: string
  createdAt: string
  lastUpdatedAt: string
  createdByUserId: string
  createdByDisplayName: string | null
  claimedByUserId: string | null
  claimedByDisplayName: string | null
  assignedToUserId: string | null
  assignedToDisplayName: string | null
  isEscalated: boolean
  commentCount: number
  /** What a validation item is about, stable across importer re-runs (e.g. `cover`, `coords`). */
  itemKey?: string | null
  escalationLevel?: TicketEscalationLevel
  /** "Owner", "Mods" or "System Moderators". */
  escalationLabel?: string
  /** Waiting on the submitter (AwaitingUser): the escalation clock is stopped. */
  escalationPaused?: boolean
  upVotes?: number
  downVotes?: number
  /** Up minus down. */
  voteScore?: number
}

export interface TicketDetailsDto {
  id: string
  type: TicketType
  priority: TicketPriority
  status: TicketStatus
  title: string
  description: string
  targetType: TargetType | null
  targetId: string | null
  createdByUserId: string
  createdByDisplayName: string | null
  createdAt: string
  claimedByUserId: string | null
  claimedByDisplayName: string | null
  claimedAt: string | null
  assignedToUserId: string | null
  assignedToDisplayName: string | null
  assignedAt: string | null
  resolvedByUserId: string | null
  resolvedByDisplayName: string | null
  resolvedAt: string | null
  resolutionNotes: string | null
  closedByUserId: string | null
  closedByDisplayName: string | null
  closedAt: string | null
  lastUpdatedAt: string
  lastUpdatedByUserId: string | null
  lastUpdatedByDisplayName: string | null
  tags: string[] | null
  isEscalated: boolean
  escalatedAt: string | null
  escalationReason: string | null
  parentTicketId: string | null
  /** What a validation item is about, stable across importer re-runs (e.g. `cover`, `coords`). */
  itemKey?: string | null
  adventureId?: string | null
  escalationLevel?: TicketEscalationLevel
  escalationLabel?: string
  escalationLevelChangedAt?: string | null
  /** Waiting on the submitter (AwaitingUser): the escalation clock is stopped. */
  escalationPaused?: boolean
  /** Shown publicly on its adventure (votable, escalates). */
  isCommunity?: boolean
  upVotes?: number
  downVotes?: number
  voteScore?: number
  /** Ownership claims: where the adventure goes if approved (claimant and moderators only). */
  claimToEntityType?: OwnerEntityType | null
  claimToEntityId?: string | null
  claimToName?: string | null
  /** Ownership claims: whether the current owner is told who the claimant is (moderators decide). */
  claimantRevealedToOwner?: boolean
  /** Ownership claims: the claimant's name (moderators, the claimant, and owners once revealed). */
  claimantDisplayName?: string | null
  /** Ownership claims: the viewer may toggle claimantRevealedToOwner (site Moderators/Admins). */
  canRevealClaimant?: boolean
  /** Ownership claims: "Claimant", "Owner" (redacted) or "Moderator". */
  claimViewerRole?: 'Claimant' | 'Owner' | 'Moderator' | null
  /** Ownership claims: the viewer may approve/reject (site staff, while open). */
  canDecideClaim?: boolean
  /** The viewer works this ticket (claim, start, resolve, reject). */
  viewerCanWork?: boolean
  /** Photos sent with the ticket itself; comment photos ride on their comment. */
  attachments: TicketAttachmentDto[]
  comments: TicketCommentDto[]
  statusHistory: TicketStatusHistoryDto[]
}

/**
 * A photo on a ticket or comment. Both links are signed and expire after about an hour; reading
 * the ticket again issues fresh ones. Only people who may read the ticket are ever given them.
 */
export interface TicketAttachmentDto {
  id: string
  url: string
  thumbnailUrl: string
  width: number
  height: number
}

export interface TicketCommentDto {
  id: string
  ticketId: string
  authorUserId: string
  authorDisplayName: string | null
  content: string
  createdAt: string
  updatedAt: string | null
  isInternal: boolean
  /** Ownership claims: which side may read it. */
  party?: TicketCommentParty
  attachments: TicketAttachmentDto[]
}

export interface TicketStatusHistoryDto {
  id: string
  oldStatus: TicketStatus
  newStatus: TicketStatus
  changedByUserId: string
  changedByDisplayName: string | null
  changedAt: string
  notes: string | null
}

export interface TicketStatisticsDto {
  ticketsByStatus: Record<TicketStatus, number>
  ticketsByPriority: Record<TicketPriority, number>
  ticketsByType: Record<TicketType, number>
  totalTickets: number
  openTickets: number
  closedTickets: number
  escalatedTickets: number
  averageResolutionTimeMinutes: number
}

// Request DTOs
export interface CreateTicketRequest {
  type: TicketType
  priority?: TicketPriority
  title: string
  description: string
  targetType?: TargetType
  targetId?: string
  tags?: string[]
  /** Validation types only: makes creation idempotent per (adventure, type, itemKey). */
  itemKey?: string
  /** Photos uploaded first through uploadTicketAttachment, at most 5. */
  attachmentIds?: string[]
}

export interface AddCommentRequest {
  content: string
  isInternal?: boolean
  /** Ownership claims, moderators only: who the comment is for. */
  party?: TicketCommentParty
  /** Photos uploaded first through uploadTicketAttachment, at most 5. */
  attachmentIds?: string[]
}

/** POST /tickets/{id}/ownership-claim/approve. */
export interface ApproveOwnershipClaimRequest {
  notes?: string
  /** Keep the adventure's current maintainers (default: they lose access). */
  keepMaintainers?: boolean
  /** Tell the previous owner who claimed it; omitted/null leaves the current setting. */
  revealClaimant?: boolean | null
}

/** POST /tickets/{id}/ownership-claim/reject. */
export interface RejectOwnershipClaimRequest {
  reason: string
  /** Tell the current owner who claimed it; omitted/null leaves the current setting. */
  revealClaimant?: boolean | null
}

export interface OwnershipClaimDecisionDto {
  ticketId: string
  adventureId: string
  approved: boolean
  newOwnerEntityType?: OwnerEntityType | null
  newOwnerEntityId?: string | null
}

export interface ResolveTicketRequest {
  resolutionNotes: string
}

/** Optional body for POST /tickets/{id}/close. `rejected` closes it as invalid/spam/duplicate and needs a reason. */
export interface CloseTicketRequest {
  reason?: string
  rejected?: boolean
}

export interface UpdatePriorityRequest {
  priority: TicketPriority
}

export interface EscalateTicketRequest {
  reason: string
}

export interface AssignTicketRequest {
  assigneeUserId: string
}

// Pagination
export interface PaginatedResponse<T> {
  items: T[]
  totalCount: number
  page: number
  pageSize: number
  totalPages: number
}

// Utility type helpers
export const TicketTypeLabels: Record<TicketType, string> = {
  [TicketType.ContentInappropriate]: 'Inappropriate Content',
  [TicketType.ContentSpam]: 'Spam',
  [TicketType.ContentDuplicate]: 'Duplicate',
  [TicketType.ContentVandalism]: 'Vandalism',
  [TicketType.ContentCopyright]: 'Copyright',
  [TicketType.LocationPrivateProperty]: 'Private Property',
  [TicketType.LocationMissing]: 'Location Missing',
  [TicketType.LocationInaccessible]: 'Location Inaccessible',
  [TicketType.LocationSafety]: 'Safety Concern',
  [TicketType.LocationOutdatedInfo]: 'Outdated info',
  [TicketType.Bug]: 'Bug Report',
  [TicketType.FeatureRequest]: 'Feature Request',
  [TicketType.Question]: 'Question/Support',
  [TicketType.AccountIssue]: 'Account Issue',
  [TicketType.TechnicalSupport]: 'Technical Support',
  [TicketType.ValidateCoverPhoto]: 'Cover photo',
  [TicketType.ValidateOnSite]: 'Verify on site',
  [TicketType.ValidateCoordinates]: 'Check coordinates',
  [TicketType.ValidateHoursAccess]: 'Hours / access',
  [TicketType.ValidateContent]: 'Content check',
  [TicketType.AdventureOwnershipClaim]: 'Ownership claim',
  [TicketType.Other]: 'Other',
}

/**
 * What a person opening a support ticket may choose.
 *
 * Not the whole enum. Moderation reasons and location reports are filed from the thing being
 * reported — offering "Vandalism" in a support form invites somebody to file a complaint about
 * content as though it were a question about the app, and it arrives with nothing attached to say
 * what they meant.
 */
export const SUPPORT_TICKET_TYPES: TicketType[] = [
  TicketType.Bug,
  TicketType.FeatureRequest,
  TicketType.Question,
  TicketType.AccountIssue,
  TicketType.TechnicalSupport,
  TicketType.Other,
]

/** What a content report may be filed as. */
export const REPORT_TICKET_TYPES: TicketType[] = [
  TicketType.ContentInappropriate,
  TicketType.ContentSpam,
  TicketType.ContentDuplicate,
  TicketType.ContentVandalism,
  TicketType.ContentCopyright,
  TicketType.Other,
]

export const TicketPriorityLabels: Record<TicketPriority, string> = {
  [TicketPriority.Low]: 'Low',
  [TicketPriority.Medium]: 'Medium',
  [TicketPriority.High]: 'High',
  [TicketPriority.Critical]: 'Critical',
}

export const TicketStatusLabels: Record<TicketStatus, string> = {
  [TicketStatus.Pending]: 'Pending',
  [TicketStatus.Claimed]: 'Claimed',
  [TicketStatus.InProgress]: 'In Progress',
  [TicketStatus.AwaitingUser]: 'Awaiting User',
  [TicketStatus.Resolved]: 'Resolved',
  [TicketStatus.Closed]: 'Closed',
  [TicketStatus.Escalated]: 'Escalated',
}

export const TargetTypeLabels: Record<TargetType, string> = {
  [TargetType.Cache]: 'Geocache',
  [TargetType.Adventure]: 'Adventure',
  [TargetType.POI]: 'Point of Interest',
  [TargetType.Location]: 'Location',
  [TargetType.Group]: 'Group',
  [TargetType.User]: 'User',
  [TargetType.Discussion]: 'Discussion',
  [TargetType.Post]: 'Post',
}

/** Ticket list filters the pool / claimed / assigned / status lists accept (Backlog 057). */
export interface TicketListFilter {
  /** Only these types (sent as `?types=30,31`). */
  types?: TicketType[]
  /** Shorthand for every validation type (sent as `?validation=true`). */
  validation?: boolean
}

/** One item in GET /api/adventures/{id}/validation. */
export interface AdventureValidationItem {
  ticketId: string
  type: TicketType
  typeLabel: string
  itemKey: string | null
  title: string
  status: TicketStatus
  priority: TicketPriority
  createdAt: string
  resolvedAt?: string | null
}

/** GET /api/adventures/{id}/validation: open items first, then up to 20 recently resolved. */
export interface AdventureValidationDto {
  adventureId: string
  openCount: number
  items: AdventureValidationItem[]
}

/**
 * Route report evidence — the published route beside the track the reporter actually walked.
 *
 * Fetched separately from the ticket rather than folded into it: a track is thousands of points
 * and every other view of a ticket would pay for carrying them.
 */
export interface RouteEvidencePoint {
  lat: number
  lng: number
  /** Accuracy in metres, when the device reported one. */
  acc?: number | null
  /** Altitude in metres. */
  ele?: number | null
  /** Epoch milliseconds of the fix. */
  t: number
}

export interface RouteEvidenceTrack {
  sessionId: string
  recordedAt: string
  pointCount: number
  durationMs?: number | null
  points: RouteEvidencePoint[]
}

export interface RouteEvidenceSection {
  sectionId: string
  name: string
  order: number
  /** [latitude, longitude] pairs, as stored. */
  points: [number, number][]
}

export interface RouteReportEvidence {
  ticketId: string
  adventureId: string
  adventureTitle: string
  sections: RouteEvidenceSection[]
  /** Newest first. Somebody who reports a route has often walked it more than once. */
  tracks: RouteEvidenceTrack[]
}
